import { ClientId, Token, companyUserAccessToken, coreUserAccessToken } from 'hooks/useToken';
import { loginRedirectUrl } from './loginRedirect';
import { endSpoofSession, getSpoofedCompanyId, mintSpoofToken } from './spoofSession';
import { refreshUserToken } from './userTokenRefresh';

export { clientConfig } from './clientConfig';

/**
 * The subset of `RequestContext` this module needs. Every generated SDK exports its own
 * nominally-distinct `RequestContext`, but they are structurally identical, so one
 * structural type lets a single helper serve every client.
 */
type AuthRequestContext = Readonly<{
  setHeaderParam: (key: string, value: string) => void;
}>;

/**
 * Attaches the token an outgoing request should carry: the spoof token whenever one
 * exists, otherwise the admin's own. `forceUserToken` opts out the routes that must
 * resolve against the admin's own identity even while switched into a company.
 *
 * Reads storage per request, so a retry issued after `handlePostAuth` refreshed a token
 * automatically carries the fresh one.
 */
export const applyRequestAuth = (context: AuthRequestContext, forceUserToken = false): void => {
  const companyToken = sessionStorage.getItem(companyUserAccessToken);
  const useSpoofToken = !!companyToken && !forceUserToken;

  context.setHeaderParam(
    'Authorization',
    `Bearer ${useSpoofToken ? companyToken : localStorage.getItem(coreUserAccessToken())}`
  );
};

/**
 * 401 recovery: make the tokens fresh, then resolve the ORIGINAL 401 context. The SDK's
 * response processor turns it into its normal ApiException ("HTTP-Code: 401 ..."), which
 * is exactly what the React Query retry predicate in `contexts/QueryClient.ts` matches —
 * the refetch goes back through `applyRequestAuth` and carries the token refreshed here.
 * Non-query callers (e.g. `UserProvider`'s /me bootstrap) see the rejection only after
 * the refresh completed, so their own retry succeeds the same way.
 *
 * There is deliberately no per-request bookkeeping and no manual replay: `ResponseContext`
 * carries no request identity, so under a concurrent 401 burst any pairing of responses to
 * recorded requests is guesswork (ENG-1065 — the losers of that race were rejected with an
 * error the retry predicate could not match, parking their queries in a permanent error
 * state even though the token had just been refreshed). Recovery branches on the CURRENT
 * auth state instead, which needs no pairing: while spoofing, re-minting the spoof token
 * heals both token classes, because `mintSpoofToken` refreshes the admin's own token first
 * when it is expiring (ENG-522).
 *
 * 403 is deliberately not handled — the page may look degraded and that's fine.
 */
const handlePostAuth = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: any,
  addTokenForClientId: (clientId: ClientId, token: Token, prefix: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  if (context.httpStatusCode !== 401) {
    return Promise.resolve(context);
  }

  const spoofedCompanyId = getSpoofedCompanyId();
  if (spoofedCompanyId) {
    // Single-flight per company: a concurrent burst shares one loginAs call.
    const newToken = await mintSpoofToken(spoofedCompanyId);
    if (!newToken) {
      // Could not re-mint — end the spoof session and explain it on the company list.
      endSpoofSession();
    }
    return Promise.resolve(context);
  }

  const userId = localStorage.getItem('user')?.toLowerCase();
  // Read the refresh token directly from localStorage rather than through getToken(),
  // which returns null when the access token is missing: a refresh must be attempted
  // whenever a refresh token exists, even if the access token was already cleared.
  const refreshToken = userId ? localStorage.getItem(`${userId}-user-core-refresh-token`) : null;

  if (refreshToken && userId) {
    try {
      const newTokens = await refreshUserToken(refreshToken); // single-flight
      if (newTokens) {
        addTokenForClientId('core', newTokens, `${userId}-user`);
        return Promise.resolve(context);
      }
    } catch {
      // Refresh itself failed — fall through to the logout below.
    }
  }

  const featureHighlightKeys = Object.keys(localStorage).filter((key) => key.startsWith('feature_highlight-'));
  const theme = localStorage.getItem('theme');
  localStorage.clear();

  featureHighlightKeys.forEach((key) => {
    localStorage.setItem(key, 'true');
  });
  if (theme) {
    localStorage.setItem('theme', theme);
  }
  return Promise.reject(context).finally(() => {
    window.location.href = loginRedirectUrl(window.location.pathname, window.location.search);
  });
};

export default handlePostAuth;
